import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ClientRow {
  id: string;
  firstName: string;
  lastName: string;
  businessName: string | null;
  email: string | null;
  phone: string | null;
  contactNature: string;
  contactType: string;
  status: string;
  entityType: string | null;
  currentBalance: number;
  lastActivityDate: string | null;
}

interface ClientsApiResponse {
  success: boolean;
  data: Array<Record<string, unknown>>;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toClientRow(raw: Record<string, unknown>): ClientRow {
  return {
    id: raw.id as string,
    firstName: (raw.first_name as string) ?? '',
    lastName: (raw.last_name as string) ?? '',
    businessName: (raw.business_name as string) ?? null,
    email: (raw.email as string) ?? null,
    phone: (raw.phone as string) ?? null,
    contactNature: (raw.contact_nature as string) ?? 'client',
    contactType: (raw.contact_type as string) ?? '',
    status: (raw.status as string) ?? 'active',
    entityType: (raw.entity_type as string) ?? null,
    currentBalance: Number(raw.current_balance ?? raw.running_balance ?? 0),
    lastActivityDate: (raw.last_activity_date as string) ?? (raw.updated_at as string) ?? null,
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * ClientLedgerSelectPage — client listing page for accessing individual ledgers.
 * Shows all clients with their current balance, filters for quick access,
 * and navigation to the full ledger view.
 *
 * Styled to match the ContactsListPage with DataTable, search, and filters.
 */
export function ClientLedgerSelectPage() {
  const navigate = useNavigate();

  // ─── Filter & Pagination State ──────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('');
  const [sort, setSort] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ─── Data Fetching ──────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'ledger-clients', search, status, page, pageSize, sort],
    queryFn: async () => {
      const response = await apiClient.get<ClientsApiResponse>('/contacts', {
        params: {
          search: search || undefined,
          contact_type_nature: 'client',
          status: status || undefined,
          page,
          pageSize,
          sortField:
            sort.column === 'name'
              ? 'first_name'
              : sort.column === 'businessName'
                ? 'business_name'
                : sort.column,
          sortDirection: sort.direction || 'asc',
          include_balance: 'true',
        },
      });
      return response.data;
    },
  });

  const clients: ClientRow[] = useMemo(() => (data?.data ?? []).map(toClientRow), [data]);

  // Apply client-side balance filter (the API doesn't know about ledger balance)
  const filteredClients = useMemo(() => {
    if (!balanceFilter) return clients;
    if (balanceFilter === 'owing') return clients.filter((c) => c.currentBalance > 0);
    if (balanceFilter === 'credit') return clients.filter((c) => c.currentBalance < 0);
    if (balanceFilter === 'zero') return clients.filter((c) => c.currentBalance === 0);
    return clients;
  }, [clients, balanceFilter]);

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns: ColumnDef<ClientRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Client Name',
        sortable: true,
        cell: (row) => (
          <div>
            <button
              type="button"
              className="text-left font-medium text-brand-600 hover:underline dark:text-brand-400"
              onClick={() => navigate(`/client-ledger/${row.id}`)}
            >
              {row.businessName || `${row.firstName} ${row.lastName}`}
            </button>
            {row.businessName && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {row.firstName} {row.lastName}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        cell: (row) => <span className="text-gray-600 dark:text-gray-400">{row.email || '—'}</span>,
      },
      {
        id: 'phone',
        header: 'Phone',
        cell: (row) => <span className="text-gray-600 dark:text-gray-400">{row.phone || '—'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => {
          const variant =
            row.status === 'active' ? 'success' : row.status === 'inactive' ? 'warning' : 'default';
          return (
            <Badge variant={variant} size="sm">
              {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: 'currentBalance',
        header: 'Balance',
        align: 'right',
        cell: (row) => (
          <span
            className={`font-semibold ${
              row.currentBalance > 0
                ? 'text-red-600 dark:text-red-400'
                : row.currentBalance < 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {formatCurrency(row.currentBalance)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        cell: (row) => (
          <Button variant="outline" size="sm" onClick={() => navigate(`/client-ledger/${row.id}`)}>
            View Ledger
          </Button>
        ),
      },
    ],
    [navigate],
  );

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const pagination: PaginationState | undefined = data?.pagination
    ? {
        page: data.pagination.page,
        pageSize: data.pagination.pageSize,
        total: data.pagination.total,
      }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const hasActiveFilters = status || balanceFilter;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Client Ledger</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View billing ledgers, account closings, and generate PDF reports for your clients.
          </p>
        </div>
        {data?.pagination && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {data.pagination.total} client{data.pagination.total !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-48">
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              placeholder="All statuses"
            />
          </div>
          <div className="w-48">
            <Select
              label="Balance"
              value={balanceFilter}
              onChange={(e) => setBalanceFilter(e.target.value)}
              options={[
                { value: 'owing', label: 'Owing (Debit)' },
                { value: 'credit', label: 'In Credit' },
                { value: 'zero', label: 'Zero Balance' },
              ]}
              placeholder="All balances"
            />
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('');
                setBalanceFilter('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Data Table */}
      <DataTable<ClientRow>
        data={filteredClients}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No clients found. Only contacts with client nature will appear here."
        filterValue={search}
        onFilterChange={handleSearchChange}
        filterPlaceholder="Search by name, email, phone, or business name..."
        onRowClick={(row) => navigate(`/client-ledger/${row.id}`)}
      />
    </div>
  );
}
